/**
 * Faction definitions — 3 factions with event-based favor system
 */

import type { FactionId } from '../state.js';

export interface FactionBonus {
  effectType: string;
  effectValue: number;
  tierRequired: number;
  descKey: string;
}

export interface FactionDef {
  id: FactionId;
  nameKey: string;
  descKey: string;
  imagePath: string;
  color: string;
  bonuses: FactionBonus[];
}

export interface FactionEventDef {
  id: string;
  factionId: FactionId;        // faction that benefits
  targetFactionId: FactionId;  // faction that loses favor
  titleKey: string;
  descKey: string;
  helpFavorGain: number;
  helpFavorLoss: number;
  ipRewardMultiplier: number;  // CPS * this = IP reward
}

export const FACTION_DEFS: FactionDef[] = [
  {
    id: 'illuminati',
    nameKey: 'faction_illuminati',
    descKey: 'faction_illuminati_desc',
    imagePath: 'images/faction-illuminati.png',
    color: '#FFD700',
    bonuses: [
      { effectType: 'cps_pct', effectValue: 0.10, tierRequired: 1, descKey: 'faction_illuminati_bonus1' },
      { effectType: 'cps_pct', effectValue: 0.15, tierRequired: 2, descKey: 'faction_illuminati_bonus2' },
      { effectType: 'conquest_speed', effectValue: 0.20, tierRequired: 4, descKey: 'faction_illuminati_bonus3' },
    ],
  },
  {
    id: 'grey',
    nameKey: 'faction_grey',
    descKey: 'faction_grey_desc',
    imagePath: 'images/faction-grey.png',
    color: '#9DC3E6',
    bonuses: [
      { effectType: 'tap_pct', effectValue: 0.15, tierRequired: 1, descKey: 'faction_grey_bonus1' },
      { effectType: 'tap_pct', effectValue: 0.20, tierRequired: 2, descKey: 'faction_grey_bonus2' },
      { effectType: 'gc_pct', effectValue: 0.25, tierRequired: 4, descKey: 'faction_grey_bonus3' },
    ],
  },
  {
    id: 'mib_resistance',
    nameKey: 'faction_mib',
    descKey: 'faction_mib_desc',
    imagePath: 'images/faction-mib.png',
    color: '#FF4444',
    bonuses: [
      { effectType: 'boss_damage', effectValue: 0.20, tierRequired: 1, descKey: 'faction_mib_bonus1' },
      { effectType: 'offline_pct', effectValue: 0.15, tierRequired: 2, descKey: 'faction_mib_bonus2' },
      { effectType: 'boss_spawn_reduce', effectValue: 0.15, tierRequired: 4, descKey: 'faction_mib_bonus3' },
    ],
  },
];

export const FAVORABILITY_TIERS = [0, 100, 300, 600, 1000];
/** Max total favorability across all 3 factions — can't max all at once */
export const MAX_TOTAL_FAVORABILITY = 2000;

export const EVENT_INTERVAL_MIN = 240_000; // 4 minutes
export const EVENT_INTERVAL_MAX = 480_000; // 8 minutes
export const EVENT_DURATION = 60_000;      // 60 seconds
export const AD_BONUS_FAVOR = 15;

export const FACTION_EVENTS: FactionEventDef[] = [
  // Illuminati events (5)
  { id: 'illu_decode', factionId: 'illuminati', targetFactionId: 'mib_resistance',
    titleKey: 'event_illu_decode_title', descKey: 'event_illu_decode_desc',
    helpFavorGain: 20, helpFavorLoss: 10, ipRewardMultiplier: 5 },
  { id: 'illu_stock', factionId: 'illuminati', targetFactionId: 'grey',
    titleKey: 'event_illu_stock_title', descKey: 'event_illu_stock_desc',
    helpFavorGain: 20, helpFavorLoss: 10, ipRewardMultiplier: 5 },
  { id: 'illu_surveillance', factionId: 'illuminati', targetFactionId: 'mib_resistance',
    titleKey: 'event_illu_surveillance_title', descKey: 'event_illu_surveillance_desc',
    helpFavorGain: 15, helpFavorLoss: 15, ipRewardMultiplier: 8 },
  { id: 'illu_meeting', factionId: 'illuminati', targetFactionId: 'mib_resistance',
    titleKey: 'event_illu_meeting_title', descKey: 'event_illu_meeting_desc',
    helpFavorGain: 25, helpFavorLoss: 15, ipRewardMultiplier: 10 },
  { id: 'illu_bribe', factionId: 'illuminati', targetFactionId: 'grey',
    titleKey: 'event_illu_bribe_title', descKey: 'event_illu_bribe_desc',
    helpFavorGain: 20, helpFavorLoss: 10, ipRewardMultiplier: 5 },

  // Grey events (5)
  { id: 'grey_experiment', factionId: 'grey', targetFactionId: 'mib_resistance',
    titleKey: 'event_grey_experiment_title', descKey: 'event_grey_experiment_desc',
    helpFavorGain: 20, helpFavorLoss: 10, ipRewardMultiplier: 5 },
  { id: 'grey_mindcontrol', factionId: 'grey', targetFactionId: 'illuminati',
    titleKey: 'event_grey_mindcontrol_title', descKey: 'event_grey_mindcontrol_desc',
    helpFavorGain: 20, helpFavorLoss: 10, ipRewardMultiplier: 5 },
  { id: 'grey_crash', factionId: 'grey', targetFactionId: 'mib_resistance',
    titleKey: 'event_grey_crash_title', descKey: 'event_grey_crash_desc',
    helpFavorGain: 15, helpFavorLoss: 15, ipRewardMultiplier: 8 },
  { id: 'grey_terraform', factionId: 'grey', targetFactionId: 'illuminati',
    titleKey: 'event_grey_terraform_title', descKey: 'event_grey_terraform_desc',
    helpFavorGain: 25, helpFavorLoss: 15, ipRewardMultiplier: 10 },
  { id: 'grey_hack', factionId: 'grey', targetFactionId: 'mib_resistance',
    titleKey: 'event_grey_hack_title', descKey: 'event_grey_hack_desc',
    helpFavorGain: 20, helpFavorLoss: 10, ipRewardMultiplier: 5 },

  // MIB events (5)
  { id: 'mib_raid', factionId: 'mib_resistance', targetFactionId: 'grey',
    titleKey: 'event_mib_raid_title', descKey: 'event_mib_raid_desc',
    helpFavorGain: 20, helpFavorLoss: 10, ipRewardMultiplier: 5 },
  { id: 'mib_safehouse', factionId: 'mib_resistance', targetFactionId: 'illuminati',
    titleKey: 'event_mib_safehouse_title', descKey: 'event_mib_safehouse_desc',
    helpFavorGain: 20, helpFavorLoss: 10, ipRewardMultiplier: 5 },
  { id: 'mib_weapons', factionId: 'mib_resistance', targetFactionId: 'grey',
    titleKey: 'event_mib_weapons_title', descKey: 'event_mib_weapons_desc',
    helpFavorGain: 15, helpFavorLoss: 15, ipRewardMultiplier: 8 },
  { id: 'mib_evacuate', factionId: 'mib_resistance', targetFactionId: 'grey',
    titleKey: 'event_mib_evacuate_title', descKey: 'event_mib_evacuate_desc',
    helpFavorGain: 25, helpFavorLoss: 15, ipRewardMultiplier: 10 },
  { id: 'mib_expose', factionId: 'mib_resistance', targetFactionId: 'illuminati',
    titleKey: 'event_mib_expose_title', descKey: 'event_mib_expose_desc',
    helpFavorGain: 20, helpFavorLoss: 10, ipRewardMultiplier: 5 },
];

export function getFactionDef(id: FactionId): FactionDef | undefined {
  return FACTION_DEFS.find(f => f.id === id);
}

export function getEventDef(eventId: string): FactionEventDef | undefined {
  return FACTION_EVENTS.find(e => e.id === eventId);
}
